import React, { useState, useEffect } from 'react'
import { Card, Button, Steps, Alert, Typography, Space, Progress, Divider, Modal } from 'antd'
import { CheckCircleOutlined, ExclamationCircleOutlined, DatabaseOutlined, ToolOutlined, SafetyOutlined } from '@ant-design/icons'
import { setupAPI } from '../services/api'

const { Title, Text, Paragraph } = Typography

interface SetupStep {
  title: string
  description: string
  status: 'wait' | 'process' | 'finish' | 'error'
  details?: string
  error?: string
}

interface DatabaseStats {
  users: number
  games: number
  categories: number
  tags: number
  isHealthy: boolean
  version?: string
  size?: number
}

const Setup: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0)
  const [isInstalling, setIsInstalling] = useState(false)
  const [isComplete, setIsComplete] = useState(false)
  const [steps, setSteps] = useState<SetupStep[]>([
    {
      title: 'Database Health Check',
      description: 'Verify database connection and check existing data',
      status: 'wait'
    },
    {
      title: 'Create Admin User',
      description: 'Set up the first administrator account',
      status: 'wait'
    },
    {
      title: 'Import Sample Games',
      description: 'Load initial game data into the database',
      status: 'wait'
    },
    {
      title: 'Finalize Setup',
      description: 'Complete system initialization and mark as ready',
      status: 'wait'
    }
  ])
  const [databaseStats, setDatabaseStats] = useState<DatabaseStats | null>(null)
  const [installationLog, setInstallationLog] = useState<string[]>([])

  const addToLog = (message: string) => {
    setInstallationLog(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`])
  }

  const updateStep = (index: number, update: Partial<SetupStep>) => {
    setSteps(prev => prev.map((step, i) => 
      i === index ? { ...step, ...update } : step
    ))
  }

  const checkDatabaseHealth = async () => {
    try {
      addToLog('Checking database health...')
      
      const currentApiUrl = import.meta.env.VITE_API_BASE_URL || 'auto-detected'
      addToLog(`API URL: ${currentApiUrl}`)
      addToLog(`Environment: ${import.meta.env.PROD ? 'Production' : 'Development'}`)
      
      // Get database stats to verify connection
      const statsResponse = await setupAPI.getDatabaseStats()
      console.log('Debug: Stats response:', statsResponse)
      
      if (statsResponse && statsResponse.data) {
        setDatabaseStats(statsResponse.data)
        addToLog(`Database connected - Users: ${statsResponse.data.users}, Games: ${statsResponse.data.games}`)
      }
      
      // Check if already initialized (has users and games)
      if (statsResponse?.data?.users > 0 && statsResponse?.data?.games > 0) {
        updateStep(0, { status: 'finish', details: 'Database already initialized' })
        addToLog('Database is already set up. Setup not needed.')
        
        // Mark setup as complete
        localStorage.setItem('system-setup-complete', 'true')
        localStorage.setItem('setup-completed-at', new Date().toISOString())
        setIsComplete(true)
        
        setTimeout(() => {
          window.location.reload()
        }, 2000)
        return true
      }
      
      updateStep(0, { status: 'finish', details: 'Database connection healthy' })
      addToLog('Database health check passed')
      return true
      
    } catch (error: any) {
      updateStep(0, { status: 'error', error: error.message })
      addToLog(`Database health check error: ${error.message}`)
      
      if (error.response) {
        addToLog(`HTTP Status: ${error.response.status}`)
        addToLog(`Response: ${JSON.stringify(error.response.data)}`)
      }
      
      // Provide helpful error messages based on environment
      if (import.meta.env.PROD) {
        if (error.message.includes('timeout')) {
          addToLog('💡 Backend service may be starting up. Please wait a moment and try again.')
        } else if (error.message.includes('Network Error') || error.message.includes('Load failed')) {
          addToLog('🔍 CORS Issue Detected: Check backend CORS configuration')
          addToLog(`Current frontend domain: ${window.location.origin}`)
          addToLog(`Backend URL: ${import.meta.env.VITE_API_BASE_URL}`)
        }
      } else {
        if (error.message.includes('timeout')) {
          addToLog('💡 Make sure the backend server is running on port 4000')
        } else if (error.message.includes('Network Error')) {
          addToLog('💡 Check if the backend API is accessible at http://localhost:4000')
        }
      }
      
      return false
    }
  }

  const createAdminUser = async () => {
    try {
      addToLog('Creating admin user...')
      updateStep(1, { status: 'process' })
      
      // Check if admin already exists
      const adminExists = await setupAPI.checkAdminExists()
      if (adminExists?.data?.adminExists) {
        updateStep(1, { status: 'finish', details: 'Admin user already exists' })
        addToLog('Admin user already exists, skipping creation')
        return true
      }
      
      addToLog('Setting up default admin account...')
      const adminResponse = await setupAPI.createAdminUser({
        username: 'admin',
        email: 'admin@gameplatform.local',
        password: 'Admin@123456', // Default password - should be changed after first login
        firstName: 'Admin',
        lastName: 'User'
      })
      
      if (adminResponse?.data?.success) {
        updateStep(1, { status: 'finish', details: 'Admin user created successfully' })
        addToLog('✅ Admin user created successfully')
        addToLog('Default credentials: admin / Admin@123456')
        addToLog('⚠️  Change these credentials immediately after first login')
        return true
      } else {
        updateStep(1, { status: 'error', error: 'Admin user creation failed' })
        addToLog('❌ Admin user creation failed')
        return false
      }
    } catch (error: any) {
      updateStep(1, { status: 'error', error: error.message })
      addToLog(`Admin user creation error: ${error.message}`)
      
      if (error.message.includes('already exists')) {
        updateStep(1, { status: 'finish', details: 'Admin user already exists' })
        addToLog('Admin user already exists')
        return true
      }
      
      return false
    }
  }

  const importSampleGames = async () => {
    try {
      addToLog('Importing sample games...')
      updateStep(2, { status: 'process' })
      
      // Check if games already exist
      if (databaseStats && databaseStats.games > 0) {
        updateStep(2, { status: 'finish', details: 'Sample games already imported' })
        addToLog('Sample games already exist, skipping import')
        return true
      }
      
      addToLog('Loading sample game data from database...')
      addToLog('This may take up to 30 seconds...')
      
      const timeoutMs = import.meta.env.PROD ? 60000 : 45000 // 60s for prod, 45s for dev
      const seedPromise = setupAPI.seedDatabase()
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Game import timeout')), timeoutMs)
      )
      
      const response = await Promise.race([seedPromise, timeoutPromise]) as any
      
      if (response?.data?.success || response?.data?.status === 'completed' || response?.data?.status === 'skipped') {
        updateStep(2, { status: 'finish', details: 'Sample games imported successfully' })
        addToLog('✅ Sample games imported successfully')
        return true
      } else {
        updateStep(2, { status: 'error', error: 'Game import failed' })
        addToLog('❌ Game import failed')
        return false
      }
    } catch (error: any) {
      updateStep(2, { status: 'error', error: error.message })
      addToLog(`Game import error: ${error.message}`)
      
      if (error.message.includes('already exists')) {
        updateStep(2, { status: 'finish', details: 'Sample games already imported' })
        addToLog('Sample games already imported')
        return true
      }
      
      if (error.message.includes('timeout')) {
        addToLog('⏱️ Import is taking longer than expected. This is normal for first-time setup.')
        addToLog('The process may continue in the background. You can try again after refreshing.')
      }
      
      return false
    }
  }

  const finalizeSetup = async () => {
    try {
      addToLog('Finalizing system setup...')
      updateStep(3, { status: 'process' })
      
      // Mark setup as complete in localStorage
      localStorage.setItem('system-setup-complete', 'true')
      localStorage.setItem('setup-completed-at', new Date().toISOString())
      
      updateStep(3, { status: 'finish', details: 'System setup complete' })
      addToLog('System setup finalized')
      setIsComplete(true)
      return true
    } catch (error: any) {
      updateStep(3, { status: 'error', error: error.message })
      addToLog(`Setup finalization error: ${error.message}`)
      return false
    }
  }

  const runInstallation = async () => {
    setIsInstalling(true)
    addToLog('Starting system installation...')
    
    try {
      // Step 1: Database Health Check
      setCurrentStep(0)
      const healthOk = await checkDatabaseHealth()
      if (!healthOk) {
        setIsInstalling(false)
        return
      }

      // Step 2: Create Admin User
      setCurrentStep(1)
      const adminOk = await createAdminUser()
      if (!adminOk) {
        setIsInstalling(false)
        return
      }

      // Step 3: Import Sample Games
      setCurrentStep(2)
      const gamesOk = await importSampleGames()
      if (!gamesOk) {
        setIsInstalling(false)
        return
      }

      // Step 4: Finalize Setup
      setCurrentStep(3)
      const setupOk = await finalizeSetup()
      if (!setupOk) {
        setIsInstalling(false)
        return
      }

      addToLog('✅ Installation completed successfully!')
      
      // Auto-refresh page after 3 seconds to remove setup page
      setTimeout(() => {
        window.location.reload()
      }, 3000)
      
    } catch (error: any) {
      addToLog(`❌ Installation failed: ${error.message}`)
    } finally {
      setIsInstalling(false)
    }
  }

  const checkExistingSetup = async () => {
    try {
      const response = await setupAPI.getDatabaseStats()
      
      if (!response?.data) {
        console.error('No data received from getDatabaseStats')
        return false
      }
      
      const stats = response.data
      
      if (stats.users > 0) {
        // System already has data, setup might be complete
        setDatabaseStats(stats)
        addToLog(`Found existing data: ${stats.users} users, ${stats.games} games, ${stats.categories} categories, ${stats.tags} tags`)
        
        // If we have a good amount of data, consider setup complete
        if (stats.users > 0 && stats.categories > 100 && stats.games > 0) {
          addToLog('Database appears to be fully seeded. Marking setup as complete.')
          localStorage.setItem('system-setup-complete', 'true')
          localStorage.setItem('setup-completed-at', new Date().toISOString())
          setIsComplete(true)
        }
        
        return true
      }
      return false
    } catch (error) {
      console.error('Error checking existing setup:', error)
      return false
    }
  }

  const resetDatabase = async () => {
    Modal.confirm({
      title: 'Reset Database',
      content: 'Are you sure you want to reset the system? This will require running setup again.',
      okText: 'Yes, Reset',
      cancelText: 'Cancel',
      okType: 'danger',
      onOk: async () => {
        try {
          addToLog('Resetting setup state...')
          localStorage.removeItem('system-setup-complete')
          localStorage.removeItem('setup-completed-at')
          addToLog('Setup state has been reset. Please refresh the page.')
          setTimeout(() => {
            window.location.reload()
          }, 1000)
        } catch (error: any) {
          addToLog(`Error: ${error.message}`)
        }
      },
    })
  }

  const forceResetSetup = () => {
    localStorage.removeItem('system-setup-complete')
    localStorage.removeItem('setup-completed-at')
    window.location.reload()
  }

  // Add this button in development mode
  const DebugControls = () => {
    if (import.meta.env.PROD) return null
    
    const testApiConnection = async () => {
      try {
        console.log('Testing API connection...')
        console.log('Environment variables:', {
          VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
          PROD: import.meta.env.PROD,
          MODE: import.meta.env.MODE
        })
        
        const response = await setupAPI.getDatabaseStats()
        console.log('API Response:', response)
        addToLog(`API Test Success: ${JSON.stringify(response?.data || 'No data')}`)
      } catch (error: any) {
        console.error('API Test Error:', error)
        addToLog(`API Test Error: ${error.message}`)
      }
    }
    
    return (
      <Alert
        type="info"
        message="Development Mode"
        description={
          <Space>
            <Button onClick={resetDatabase} size="small" danger>
              Reset Setup
            </Button>
            <Button onClick={testApiConnection} size="small">
              Test API Connection
            </Button>
          </Space>
        }
        style={{ marginBottom: '16px' }}
      />
    )
  }

  // Simplified setup guide component
  const SetupGuide = () => (
    <Alert
      type="info"
      showIcon
      message="System Setup Required"
      description={
        <div style={{ marginTop: '12px' }}>
          <Paragraph>
            This wizard will help you set up the Gaming Platform for the first time:
          </Paragraph>
          <ol style={{ marginBottom: 0 }}>
            <li><strong>Database Check:</strong> Verify database connection and existing data</li>
            <li><strong>Admin Account:</strong> Create the first administrator user</li>
            <li><strong>Sample Games:</strong> Load initial game data into the system</li>
            <li><strong>Finalization:</strong> Complete the setup process</li>
          </ol>
        </div>
      }
      style={{ marginBottom: '16px' }}
    />
  )

  // Add a debug function to test seed endpoint
  const testSeedEndpoint = async () => {
    try {
      addToLog('Testing game import endpoint...')
      const response = await setupAPI.seedDatabase()
      console.log('Game import test result:', response)
      addToLog(`Result: ${JSON.stringify(response?.data || 'No data')}`)
    } catch (error: any) {
      console.error('Test error:', error)
      addToLog(`Test error: ${error.message}`)
    }
  }
    
      useEffect(() => {
        checkExistingSetup()
      }, [])
    
      return (
        <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
          <Card>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div style={{ textAlign: 'center' }}>
                <Title level={2}>
                  <ToolOutlined style={{ marginRight: '8px' }} />
                  Gaming Platform Setup
                </Title>
                <Paragraph>
                  Welcome to the Gaming Platform admin portal setup. This wizard will guide you through
                  the initial system configuration and database setup.
                </Paragraph>
              </div>
    
              <DebugControls />

              <SetupGuide />

              {databaseStats && (
                <Alert
                  type="info"
                  message="Current Database Status"
                  description={
                    <div>
                      <Text>Users: {databaseStats.users} | Games: {databaseStats.games} | Categories: {databaseStats.categories} | Tags: {databaseStats.tags}</Text>
                      <br />
                      <Text type={databaseStats.isHealthy ? 'success' : 'danger'}>
                        Database Health: {databaseStats.isHealthy ? 'Healthy' : 'Unhealthy'}
                      </Text>
                    </div>
                  }
                />
              )}
    
              <Steps
                current={currentStep}
                items={steps.map((step, index) => ({
                  title: step.title,
                  description: step.description,
                  status: step.status,
                  icon: step.status === 'finish' ? <CheckCircleOutlined /> : 
                        step.status === 'error' ? <ExclamationCircleOutlined /> : undefined
                }))}
              />
    
              {steps.some(step => step.error) && (
                <Alert
                  type="error"
                  message="Setup Errors"
                  description={
                    <ul>
                      {steps.map((step, index) => 
                        step.error ? <li key={index}><strong>{step.title}:</strong> {step.error}</li> : null
                      )}
                    </ul>
                  }
                />
              )}
    
              <Card 
                title={
                  <Space>
                    <DatabaseOutlined />
                    Installation Progress
                  </Space>
                }
                size="small"
              >
                <div style={{ maxHeight: '300px', overflowY: 'auto', backgroundColor: '#f5f5f5', padding: '12px', borderRadius: '4px', fontFamily: 'monospace', fontSize: '12px' }}>
                  {installationLog.map((log, index) => (
                    <div key={index} style={{ marginBottom: '4px' }}>
                      {log}
                    </div>
                  ))}
                  {installationLog.length === 0 && (
                    <Text type="secondary">No installation logs yet...</Text>
                  )}
                </div>
              </Card>
    
              <Space style={{ width: '100%', justifyContent: 'center' }}>
                {!isComplete && (
                  <Button
                    type="primary"
                    size="large"
                    loading={isInstalling}
                    disabled={isInstalling}
                    onClick={runInstallation}
                    icon={<ToolOutlined />}
                  >
                    {isInstalling ? 'Installing...' : 'Start Installation'}
                  </Button>
                )}
                
                {isComplete && (
                  <Alert
                    type="success"
                    message="Setup Complete!"
                    description="The gaming platform has been successfully set up. The page will reload automatically."
                    showIcon
                    icon={<CheckCircleOutlined />}
                  />
                )}
              </Space>
            </Space>
          </Card>
        </div>
      )
    }
    
    export default Setup
